import { SUPERADMIN_SIDEBAR } from './superadminSidebar'
import { TELESALES_SIDEBAR } from './telesalesSidebar'
import { ECOM_MANAGER_SIDEBAR } from './ecomManagerSidebar'
import { ADMIN_SIDEBAR } from './adminSidebar'
import { INTERNAL_AUDITOR_SIDEBAR } from './internalAuditorSidebar'
import { SENIOR_ACCOUNTANT_SIDEBAR } from './seniorAccountantSidebar'
import { SENIOR_ACCOUNTANT_2_SIDEBAR } from './seniorAccountant2Sidebar'
import { FINANCE_MANAGER_SIDEBAR } from './financeManagerSidebar'
import { FIELD_SALES_SIDEBAR } from './fieldSalesSidebar'
import { MANAGER_SIDEBAR } from './managerSidebar'
import { MODERATOR_SIDEBAR } from './moderatorSidebar'
import { SALES_SUPERVISOR_SIDEBAR } from './salesSupervisorSidebar'
import { SALES_ADMIN_SIDEBAR } from './salesAdminSidebar'
import { JUNIOR_ACCOUNTANT_SIDEBAR } from './juniorAccountantSidebar'
import { ACCOUNTS_TRAINEE_SIDEBAR } from './accountsTraineeSidebar'
import { HR_MANAGER_SIDEBAR } from './hrManagerSidebar'
import { ACCOUNTANT_SIDEBAR } from './accountantSidebar'
import { OPERATIONS_SIDEBAR } from './operationsSidebar'
import { FINANCE_SIDEBAR } from './financeSidebar'
import { STOREKEEPER_SIDEBAR } from './storekeeperSidebar'
import { PACKER_SIDEBAR } from './packerSidebar'
import { BILLER_SIDEBAR } from './billerSidebar'

/**
 * Map roles to their corresponding sidebar configurations.
 * A user can have multiple roles (comma-separated in menuroles).
 * Priority is determined by role hierarchy: superadmin > admin > finance_manager > senior_accountant_2 > hr_manager > internal_auditor > senior_accountant > manager > moderator > sales_admin > sales_supervisor > accountant > junior_accountant > accounts_trainee > field_staff > operations > finance > ecom_manager > storekeeper > packer > biller > telesales
 */
export const ROLE_SIDEBAR_MAP = {
  'superadmin': SUPERADMIN_SIDEBAR,
  'admin': ADMIN_SIDEBAR,
  'finance_manager': FINANCE_MANAGER_SIDEBAR,
  'internal_auditor': INTERNAL_AUDITOR_SIDEBAR,
  'senior_accountant': SENIOR_ACCOUNTANT_SIDEBAR,
  'senior_accountant_2': SENIOR_ACCOUNTANT_2_SIDEBAR,
  'hr_manager': HR_MANAGER_SIDEBAR,
  'manager': MANAGER_SIDEBAR,
  'moderator': MODERATOR_SIDEBAR,
  'sales_admin': SALES_ADMIN_SIDEBAR,
  'sales_supervisor': SALES_SUPERVISOR_SIDEBAR,
  'accountant': ACCOUNTANT_SIDEBAR,
  'junior_accountant': JUNIOR_ACCOUNTANT_SIDEBAR,
  'accounts_trainee': ACCOUNTS_TRAINEE_SIDEBAR,
  'field_staff': FIELD_SALES_SIDEBAR,
  'operations': OPERATIONS_SIDEBAR,
  'finance': FINANCE_SIDEBAR,
  ecom_manager: ECOM_MANAGER_SIDEBAR,
  'storekeeper': STOREKEEPER_SIDEBAR,
  'packer': PACKER_SIDEBAR,
  'biller': BILLER_SIDEBAR,
  'telesales': TELESALES_SIDEBAR,
}

const PRIORITY_ORDER = ['superadmin', 'admin', 'finance_manager', 'senior_accountant_2', 'hr_manager', 'internal_auditor', 'senior_accountant', 'manager', 'moderator', 'sales_admin', 'sales_supervisor', 'accountant', 'junior_accountant', 'accounts_trainee', 'field_staff', 'operations', 'finance', 'ecom_manager', 'storekeeper', 'packer', 'biller', 'telesales']

/**
 * Get the appropriate sidebar configuration for a user's roles.
 * Takes the highest-priority role the user has.
 *
 * @param {string} rolesString - Comma-separated roles (e.g., "telesales,user")
 * @returns {array} Sidebar configuration for the highest-priority role
 */
export function getSidebarForRoles(rolesString) {
  if (!rolesString || typeof rolesString !== 'string') {
    return TELESALES_SIDEBAR
  }

  const roles = rolesString
    .split(',')
    .map(r => r.trim().toLowerCase())
    .filter(Boolean)

  for (const role of PRIORITY_ORDER) {
    if (roles.includes(role) && ROLE_SIDEBAR_MAP[role]) {
      return ROLE_SIDEBAR_MAP[role]
    }
  }

  return TELESALES_SIDEBAR
}

/**
 * Nav items that aren't part of any role sidebar but should still appear for
 * a given user. Kept here (rather than inside TheSidebar) so every consumer of
 * the nav — the sidebar itself and the spotlight catalog — sees the same list.
 *
 * Returns a fresh array so the imported (shared) sidebar constants are never
 * mutated; repeated calls would otherwise append duplicates.
 *
 * @param {array} sidebar - Base sidebar config
 * @param {number|null} userId - Signed-in user id
 * @returns {array}
 */
export function withUserNavExtras(sidebar, userId) {
  const items = Array.isArray(sidebar) ? sidebar.slice() : []

  // Every staff member can see their own biometric attendance, regardless of
  // role — the API scopes it to their own mapped PIN.
  //
  // `section` is ignored by the sidebar (which groups by the preceding `title`
  // entry) and read by the spotlight catalog, which would otherwise file these
  // trailing extras under whatever section happened to come last.
  items.push({
    slug: 'link',
    name: 'My Attendance',
    href: '/attendance/my',
    icon: 'cilClock',
    section: 'Attendance',
  })

  // Karnataka customers is a single-user page (see the matching beforeEnter
  // guard on the route).
  if (Number(userId) === 84) {
    items.push({
      slug: 'link',
      name: 'Karnataka Customers',
      href: '/karnataka-customers',
      icon: 'cilLocationPin',
      section: 'CRM & Calls',
    })
  }

  return items
}

const ROLE_LANDING_MAP = {
  'packer': '/shipping/dashboard',
  'biller': '/pos',
}

/**
 * Get the post-login landing path for a user's roles. Roles that don't have
 * an explicit landing fall back to /dashboard (admin/superadmin/etc.).
 *
 * @param {string} rolesString - Comma-separated roles
 * @returns {string} Path to redirect the user to after login
 */
export function getLandingForRoles(rolesString) {
  if (!rolesString || typeof rolesString !== 'string') {
    return '/dashboard'
  }

  const roles = rolesString
    .split(',')
    .map(r => r.trim().toLowerCase())
    .filter(Boolean)

  for (const role of PRIORITY_ORDER) {
    if (roles.includes(role)) {
      return ROLE_LANDING_MAP[role] || '/dashboard'
    }
  }

  return '/dashboard'
}
